import { Icon } from "@/components/Icon";
import type { QueuedPunch } from "@/offline/outboxStore";
import "./PendingPunchStatus.css";

interface PendingPunchStatusProps {
  items: QueuedPunch[];
}

/**
 * ef-017 — the punch-clock counterpart to `OutboxStatus.tsx`, and deliberately the same
 * shape: `role="status"` (a polite live region, never `role="alert"` — nothing failed,
 * the tap is just waiting its turn) and `data-state="pending"` (ui-breadcrumbs #1), read
 * TOGETHER as this screen's ground truth for "something is queued" rather than inferred
 * from the wifi-off icon or which button happens to be showing.
 *
 * ALWAYS EXACTLY ONE ELEMENT, however many punches are queued (0, 1, or — for the
 * in-then-out-while-still-offline case — 2). offline-punch.spec.ts's own `pendingPunch`
 * locator combines role and attribute on ONE element and calls `.toBeVisible()` on it,
 * which is a Playwright strict-mode failure the moment two elements match. So this
 * renders one summary line for the whole queue rather than one row per item — the same
 * choice `OutboxStatus` makes for a count of queued entries.
 */
export function PendingPunchStatus({ items }: PendingPunchStatusProps) {
  if (items.length === 0) return null;

  return (
    <div className="pending-punch" role="status" data-state="pending">
      <span className="pending-punch__icon" aria-hidden="true">
        <Icon name="wifi-off" size={16} />
      </span>
      <span className="pending-punch__text">{describeQueue(items)}</span>
    </div>
  );
}

/** "Clocked in at 6:02 — saved on this phone, will send when connected." for the common
 *  one-item case; the AC3 in-then-out case (both still queued, e.g. a proxy outage
 *  spanning both taps) joins the two into one sentence rather than rendering twice. */
function describeQueue(items: QueuedPunch[]): string {
  const clauses = items.map(
    (item) => `${item.kind === "in" ? "Clocked in" : "Clocked out"} at ${formatPunchTime(item.recordedAt)}`,
  );
  return `${clauses.join(" and ")} — saved on this phone, will send when connected.`;
}

/** Exported for `TimeClock.tsx`'s refusal alert, which names the same tap time in the
 *  same words — one formatter, so the pending banner and the refusal that replaces it
 *  can never disagree about how a timestamp reads. */
export function formatPunchTime(recordedAt: string): string {
  return new Date(recordedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
